import React, { useEffect, useRef } from "react";
import * as Chart from "chart.js";
import HeadingComponent from "../shared/heading";

const {
  Chart: ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend,
} = Chart;
ChartJS.register(CategoryScale, LinearScale, BarElement, BarController, Title, Tooltip, Legend);

function HorizontalBarChart({ chartData, colors, years, yAxisLabel, top5Data }) {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (chartRef.current && years.length > 0 && chartData.length > 0) {
      const ctx = chartRef.current.getContext("2d");
      if (chartInstance.current) chartInstance.current.destroy();

      const themeColors = {
        textColor: "#e5e7eb",
        gridColor: "rgba(255, 255, 255, 0.1)",
        borderColor: "#FFFFFF",
      };

      let overallMax = 0;

      chartData.forEach((item) => {
        let stackTotal = 0;

        years.forEach((year) => {
          stackTotal += item[`y${year}`] || 0;
        });

        if (stackTotal > overallMax) {
          overallMax = stackTotal;
        }
      });

      const labels = chartData.map((d) => d.duration);
      const datasetColors = [colors.chartBlueLight, colors.chartOrangeLi, colors.chartGray];

      const datasets = years.map((year, index) => ({
        label: year.toString(),
        data: chartData.map((d) => d[`y${year}`] || 0),
        backgroundColor: datasetColors[index] || colors.chartGray,
        borderWidth: 0,
        barThickness: 8,
      }));

      chartInstance.current = new ChartJS(ctx, {
        type: "bar",
        data: { labels, datasets },
        options: {
          indexAxis: "y",
          responsive: false,
          maintainAspectRatio: false,
          devicePixelRatio: Math.max(2, window.devicePixelRatio || 1),
          plugins: {
            legend: { display: false },
            tooltip: {
              enabled: false,
              callbacks: {
                label: function (context) {
                  let label = context.dataset.label || "";
                  if (label) label += ": ";
                  if (context.parsed.x !== null) label += context.parsed.x.toLocaleString();
                  return label;
                },
              },
            },
          },
          scales: {
            x: {
              stacked: true,
              max: Math.ceil(overallMax * 1.1),
              ticks: {
                color: themeColors.textColor,
                font: { size: 9 },
                stepSize: Math.ceil(overallMax / 5),
              },
              grid: { display: false, drawBorder: false },
            },
            y: {
              stacked: true,
              ticks: { color: themeColors.textColor, font: { size: 9, weight: "bold" } },
              grid: { display: false, drawBorder: false },

              title: {
                display: true,
                text: yAxisLabel,
                color: themeColors.textColor,
                font: { size: 11 },
              },
            },
          },
          layout: {
            padding: {
              right: 20,
              left: 10,
            },
          },
        },
        plugins: [
          {
            id: "customLabels",
            afterDatasetsDraw: (chart) => {
              const ctx = chart.ctx;

              // Sirf pehli row ki pehli year ki duration uthao
              const top1Duration = top5Data?.[0]?.[`time${years[0]}`]?.toString() || null;

              chart.data.datasets.forEach((dataset, datasetIndex) => {
                const meta = chart.getDatasetMeta(datasetIndex);

                meta.data.forEach((bar, index) => {
                  const label = chart.data.labels[index];
                  const value = dataset.data[index];

                  if (top1Duration && label?.toString() === top1Duration && value > 0) {
                    const pos = bar.tooltipPosition();

                    ctx.save();
                    ctx.fillStyle = themeColors.textColor;
                    ctx.font = "12px Arial";
                    ctx.textAlign = "left";
                    ctx.textBaseline = "middle";
                    ctx.fillText(value.toLocaleString(), pos.x - 70, pos.y);
                    ctx.restore();
                  }
                });
              });
            },
          },
        ],
      });
    }

    let resizeTimeout = null;
    const resizeObserver = new ResizeObserver(() => {
      if (!containerRef.current || !chartInstance.current) return;
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const w = containerRef.current.offsetWidth;
        const h = containerRef.current.offsetHeight;
        chartInstance.current.resize(w, h);
      }, 150);
    });
    if (containerRef.current) resizeObserver.observe(containerRef.current);

    return () => {
      clearTimeout(resizeTimeout);
      resizeObserver.disconnect();
      if (chartInstance.current) chartInstance.current.destroy();
    };
  }, [chartData, colors, years]);

  return (
    <div ref={containerRef} style={{ width: "100%", height: "100%" }}>
      <canvas ref={chartRef}></canvas>
    </div>
  );
}

const YEAR_INDICATOR_COLORS = ["#60a5fa", "#fb923c", "#9ca3af"];

export default function ParkingChartComponent({
  chartData,
  top5Data,
  bottomTableData,
  years = [],
  colors,
  mainTitle,
  subTitle,
  chartTitle,
  chartSubTitle,
  bottomLabel,
  yAxisLabel = "PARKING DURATION HRS",
}) {
  return (
    <div className="text-foreground transition-colors px-5" style={{ backgroundColor: "#ffffff" }}>
      
      <div className="flex flex-col gap-0.5 mb-2">
        {mainTitle && <HeadingComponent variant={"main"}>{mainTitle}</HeadingComponent>}
        {subTitle && <HeadingComponent variant={"sub"}>{subTitle}</HeadingComponent>}
      </div>

      <div className="" style={{ backgroundColor: "#464647", borderRadius: "8px" }}>
        <div className="relative">
          <div className="text-center pt-2">
            {chartTitle && (
              <div className="font-normal" style={{ color: "#ffffff", fontSize: "13px" }}>
                {chartTitle}
              </div>
            )}
            {chartSubTitle && (
              <div className="mt-1" style={{ color: "#ffffff", fontSize: "13px" }}>
                {chartSubTitle}
              </div>
            )}
          </div>

          <div
            className="absolute top-3 right-2"
            style={{
              backgroundColor: "#ffffff",
              border: `1.5px solid ${colors.black}`,
              fontSize: "11px",
              width: years.length === 2 ? "170px" : "350px",
            }}
          >
            <table className="w-full !border-collapse">
              <thead>
                <tr>
                  <th colSpan={6} style={{ backgroundColor: "white", color: colors.black }}>
                    Top 5 Durations based on Avg monthly transaction
                  </th>
                </tr>
                <tr style={{ backgroundColor: colors.black }}>
                  {years.map((year) => (
                    <React.Fragment key={year}>
                      <th
                        className="!border py-0.5 px-1 font-bold text-[9px]"
                        style={{ borderColor: colors.gray, color: colors.white }}
                      >
                        Duration
                      </th>
                      <th
                        className="!border py-0.5 px-1 font-bold text-[9px]"
                        style={{ borderColor: colors.gray, color: colors.white }}
                      >
                        {year}
                      </th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {top5Data.map((row, idx) => (
                  <tr key={idx}>
                    {years.map((year) => (
                      <React.Fragment key={year}>
                        <td
                          className="!border py-1 px-1 text-center font-bold text-[9px]"
                          style={{
                            backgroundColor: colors.navyDark,
                            color: colors.white,
                            borderColor: colors.black,
                          }}
                        >
                          {row[`time${year}`]}
                        </td>
                        <td
                          className="!border py-1 px-1 text-right text-[9px]"
                          style={{
                            backgroundColor: colors.tableBlueLight,
                            borderColor: colors.black,
                            color: colors.black,
                          }}
                        >
                          {row[`value${year}`] || ""}
                        </td>
                      </React.Fragment>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6" style={{ height: "400px" }}>
            <HorizontalBarChart
              chartData={chartData}
              colors={colors}
              years={years}
              yAxisLabel={yAxisLabel}
              top5Data={top5Data}
            />
          </div>

          <div className="mt-1 px-2">
            <table
              className="w-full !border-collapse"
              style={{ fontSize: "9px", tableLayout: "fixed" }}
            >
              <colgroup>
                <col style={{ width: "45px" }} />
                {bottomTableData.map((_, idx) => (
                  <col key={idx} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  <th className="px-0.5 py-0.5 font-bold" style={{ color: "#ffffff" }}></th>
                  {bottomTableData.map((item, idx) => (
                    <th
                      key={idx}
                      className="!border px-0.5 py-0.5 font-normal text-center overflow-hidden text-ellipsis whitespace-nowrap"
                      style={{ color: "#ffffff", borderColor: "#ffffff" }}
                    >
                      {item.duration}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {years.map((year, yearIdx) => (
                  <tr key={year}>
                    <td
                      className="!border px-0.5 py-0.5 font-bold overflow-hidden"
                      style={{ color: "#ffffff", borderColor: "#ffffff" }}
                    >
                      <span style={{ display: "flex", alignItems: "center", gap: "3px" }}>
                        <span
                          style={{
                            display: "inline-block",
                            width: "6px",
                            height: "6px",
                            borderRadius: "50%",
                            backgroundColor: YEAR_INDICATOR_COLORS[yearIdx] || "#ffffff",
                            flexShrink: 0,
                          }}
                        />
                        {year}
                      </span>
                    </td>
                    {bottomTableData.map((item, idx) => (
                      <td
                        key={idx}
                        className="!border px-0.5 py-0.5 text-center overflow-hidden text-ellipsis whitespace-nowrap"
                        style={{ color: "#ffffff", borderColor: "#ffffff" }}
                      >
                        {item[`y${year}`]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="text-center py-2">
            <div className="font-semibold" style={{ color: "#ffffff", fontSize: "9px" }}>
              {bottomLabel || "MONTHLY AVERAGE TRANSACTION"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
